<template>
<div class="cart-page">
    <div class="cart-page__title-wrapper">
        <h1 class="headline-xl">Your Cart</h1>
        <p class="paragraph-feature">{{ list.total_quantity }} Items<span class="mobile-only" v-if="canSeePricing">{{ currencySymbol }}{{ subTotal }}</span></p>
    </div>
    <div v-if="list.total_skus">
        <div class="cart-page__two-columns">
            <div class="first-column">
                <div class="cart-page__featured-message-wrapper" v-if="isPropertyVisible('free_shipping_msg')">
                    <div class="cart-page__featured-message shipping-info" :style="shippingInfoStyle">
                        <FreeShipping class="shipping-icon" alt="Free shipping"/>
                        <p class="title-xs">{{ freeShippingMessage }}</p>
                    </div>

                </div>

                <div class="cart-page__featured-message-wrapper" v-if="isPropertyVisible('b2b_cart_msg')">
                <div class="cart-page__featured-message">
                        <p class="paragraph-details">The contents of your cart are automatically saved to the Lists section of your account when logged in.</p>
                    </div>
                </div>

                <VirtualScroller class="products_row product_row cart_product_row">
                    <ListItem
                        v-for="(listItem, index) in list_items"
                        :key="index"
                        :list_item="listItem"
                        @updateItemQuantity="updateItemQuantity(index, $event)"
                        @listItemError="handleListItemErrors(index, $event)"
                    />
                </VirtualScroller>
    
                <div class="cart-page__cart-actions">
                    <a :href="route('cart.print')" class="print-link" aria-label="Print your cart">
                        <PrintIcon alt="Print" /> Print Cart
                    </a>
                    <a aria-label="Clear your cart" @click="openListDelete()">
                        <TrashIcon alt="Clear Cart" />Clear Cart
                    </a>
                </div>
            </div>
    
            <div v-if="canSeePricing" class="second-column">
                <aside class="summary-content">
                    <CartSummary :list="list" :is-tax-exempt="isTaxExempt"/>
                    <div class="summary-links">
                        <div class="links-wrapper">
                            <Link target="_blank" :href="user.is_dealer ? route('customer-service.cms', ['wholesale-shipping-information']) : route('index.cms', ['shipping-info-dtc']) " aria-label="View shipping information">Shipping Information</Link>
                            <Link v-if="!user.is_dealer" target="_blank" :href="route('customer-service.cms', ['consumer-return-policy'])" aria-label="View return policy">Return Policy</Link>
                        </div>
                        <Link target="_blank" :href="route('customer-service.index')" aria-label="Get help with your order">Need Help?</Link>
                    </div>
                </aside>
            </div>
        </div>
        <ProductCarousel :heading="{text: 'Recommended For You'}" :cart-products="cartProducts" boost-tag="view-cart" allow-quick-view/>
    </div>
    <div class="cart-page" v-else>
        <div class="cms-feature">
            <h4 class="headline-l results-header text-center"> No items in your cart</h4>
        </div>
        <ProductCarousel :heading="{text: 'Recommended For You'}" boost-tag="no-results" allow-quick-view/>

    </div>
</div>
</template>
<script lang="ts" setup>
    import { ref, computed, provide, reactive, inject } from "vue";
    import { useCartDetails } from "@/helpers/composables/useCartDetails";
    import ListItem from "@/components/cart/ListItem.vue";
    import CartSummary from "@/components/cart/CartSummary.vue";
    import ListDeleteModal from "@/components/ListDeleteModal.vue";
    import { Link, router } from "@inertiajs/vue3";
    // import { route } from "ziggy-js";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { useDialog } from 'primevue/usedialog';
    import ProductCarousel from "@/components/ProductCarousel.vue";
    import FreeShipping from "@icons/free-shipping.svg";
    import PrintIcon from "@icons/print-icon.svg";
    import TrashIcon from "@icons/red-trash-icon.svg";
    import VirtualScroller from "@/components/VirtualScroller.vue";
    const dialog = useDialog();
    let route = inject("route");
    interface List {
        list_id: number;
        name?: string;
        total?: number;
        amonut?: number;
    }

    let { list, list_items } = defineProps({
        list: {
            type: Object as PropType<List>,
            default: {}
        },
        list_items: {
            type: Array<any>,
            default: {}
        },
        isTaxExempt: {
            type: Boolean,
            default: false
        }
    });

    const itemList = computed(() => {
        return list_items;
    });

    
    let openListDelete = () => {
        dialog.open(ListDeleteModal, {
            props: {
                showHeader: false,
                modal: true
            },
            data: {
                clearCart: true,
                type:"list",
                list_id: list.list_id,
                list_item_id: '',
                beforeCloseForList(ctx) {
                    return new Promise((resolve) => {
                        router.reload();
                        resolve();
                    });
                }
            }
        });
    };

    const afterDelete = computed(() => {
        return route("cart.show");
    });

    let { isPropertyVisible, currencySymbol } = useCartDetails();

    let settingsStore = useSettingsStore();
    let { freeShippingMessage, freeShippingProgressPercentage, user, canSeePricing, canPlaceOrders } = storeToRefs(settingsStore);

    const shippingInfoStyle = computed(() =>  {
        return {
            '--shipping-progress': `${freeShippingProgressPercentage.value}%`
        };
    });

    let updateItemQuantity = function (index, $event: Event) {
        list_items[index].quantity = $event.value.quantity;
    };

    let subTotal = computed(() => {
        return parseFloat(list.total ?? 0).toFixed(2);
    });

    const listItemErrors = reactive({});

    provide('listItemErrors', listItemErrors);

    const handleListItemErrors = (index, $event) => {   
      if ($event.outOfStock) {
        listItemErrors[$event.index] = true;
      } else {
        delete listItemErrors[$event.index];
      }
    };


    let cartProducts = computed(()=>{
        return list_items.map((item)=>{
            return item.product_line_code
        })
    })
</script>

<style scoped> 
    .cart-page__featured-message-wrapper .shipping-info::after {
        width: var(--shipping-progress);
    }

</style>
